/**
 * Simulation-related type definitions for PAL Configuration
 */

import type { ConfigurationRuleResponse } from "./api";

// Range value for BETWEEN operator
export interface RangeValue {
  from?: number | string | null;
  to?: number | string | null;
  min?: number | string | null;
  max?: number | string | null;
}

// Parameter value types
export type ParameterValue =
  | string
  | number
  | boolean
  | string[]
  | number[]
  | RangeValue
  | null;

// Parameter values map
export interface ParameterValues {
  [fieldName: string]: ParameterValue;
}

// Operator values map
export interface OperatorValues {
  [fieldName: string]: string;
}

// Simple condition JSON structure
export interface SimpleCondition {
  type: "CONDITION";
  field: string;
  operator: string;
  value: ParameterValue;
}

// Between condition JSON structure
export interface BetweenCondition {
  type: "CONDITION";
  field: string;
  operator: "BETWEEN";
  value: {
    from: number | string | null;
    to: number | string | null;
  };
}

// Condition group JSON structure
export interface ConditionGroup {
  type: "GROUP";
  operator: "AND" | "OR";
  rules: (ConditionJSON | ConditionGroup)[];
}

// Union type for conditions
export type ConditionJSON = SimpleCondition | BetweenCondition;
export type RuleJSON = ConditionJSON | ConditionGroup;

// Simulation result row (workstream level)
export interface SimulationResultRow {
  workstream: string;
  currentRecords: number;
  newRecords: number;
  dollarImpact: number;
}

// Full simulation results
export interface SimulationResults {
  dealerName: string;
  dealerCode: string;
  simulationName: string;
  summary: string;
  rows: SimulationResultRow[];
  rules: ConfigurationRuleResponse[];
}

// Helper type guard to check if value is a RangeValue
export const isRangeValue = (val: ParameterValue): val is RangeValue => {
  if (val === null || typeof val !== "object") return false;
  return ("from" in val && "to" in val) || ("min" in val && "max" in val);
};

/**
 * Format default value for display in table
 */
export const formatDefaultValue = (defaultValue?: {
  value?: unknown;
}): string => {
  if (!defaultValue?.value) return "—";

  const value = defaultValue.value;

  if (Array.isArray(value)) {
    return (value as string[]).join(", ");
  }

  if (typeof value === "object" && value !== null) {
    const objValue = value as { from?: number | string; to?: number | string };
    if (objValue.from !== undefined) {
      return `${objValue.from} - ${objValue.to}`;
    }
    return JSON.stringify(value);
  }

  return String(value);
};

// Import operator mapping from centralized utility to avoid duplication
import { mapOperatorToJSON } from "@/shared/utils/operatorUtils";

/**
 * Generate condition JSON for a field
 */
export const generateConditionJSON = (
  fieldName: string,
  value: ParameterValue,
  operator: string,
): ConditionJSON | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const mappedOperator = mapOperatorToJSON(operator);

  // Handle BETWEEN operator
  if (mappedOperator === "BETWEEN" && isRangeValue(value)) {
    const fromVal = value.from ?? value.min;
    const toVal = value.to ?? value.max;
    return {
      type: "CONDITION",
      field: fieldName,
      operator: "BETWEEN",
      value: {
        from: fromVal ?? null,
        to: toVal ?? null,
      },
    };
  }

  return {
    type: "CONDITION",
    field: fieldName,
    operator: mappedOperator,
    value: value,
  };
};
